/**
 * AI-assisted PDF intake body — shared by /projects/new/pdf (page) and the
 * calendar modal. Drag in the sales order / packing slip / design package; the
 * server extracts structured fields (Gemini, or a dev stub); the user reviews
 * the fused <ProjectDraftReview> and confirms; the PDFs + manifest persist to
 * the new project. Page variant adds the route-based IntakeHeader; both can hand
 * off to the chat method via onContinueInChat.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  CheckCircle2,
  ChevronDown,
  FileText,
  Loader2,
  MessageSquareText,
  Sparkles,
  Trash2,
  TriangleAlert,
  Upload,
} from 'lucide-react';
import { toast } from 'sonner';
import type {
  IntakeProposedTask,
  ProjectCreateInput,
  ProjectLineItemCreateInput,
} from '@vari-crew/schemas';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { useCreateProject } from '@/api/projects';
import { uploadProjectDocument } from '@/api/attachments';
import { persistProjectLineItems } from '@/api/lineItems';
import { useCurrentUser } from '@/auth/useCurrentUser';
import {
  recordExtractionCorrections,
  useExtractPdfs,
  useIntakeMode,
  type ExtractResponse,
} from '@/api/intake';
import { ProjectDraftReview } from '@/components/intake/ProjectDraftReview';
import type { TimingModel } from '@/components/intake/timing/timingModel';
import { IntakeHeader } from '@/components/intake/IntakeHeader';
import { draftToCreateInput } from '@/lib/intakeDraft';
import { diffDraftCorrections } from '@/lib/intakeCorrections';
import { clearIntakeSession } from '@/lib/intakeSession';
import { cn } from '@/lib/cn';
import {
  applySeed,
  useIntakeState,
  type IntakePanelProps,
  type PdfChatHandoff,
} from '@/components/intake/intakePanelShared';

const KIND_LABEL = {
  sales_order: 'Sales order',
  packing_slip: 'Packing slip',
  design_package: 'Design package',
  unknown: 'Unknown',
} as const;

const KIND_TINT = {
  sales_order: 'bg-sky-100 text-sky-900 border-sky-200',
  packing_slip: 'bg-purple-100 text-purple-900 border-purple-200',
  design_package: 'bg-emerald-100 text-emerald-900 border-emerald-200',
  unknown: 'bg-amber-100 text-amber-900 border-amber-200',
} as const;

export function PdfIntakePanel({
  variant,
  seed,
  onCreated,
  onDirtyChange,
  onContinueInChat,
}: IntakePanelProps & { onContinueInChat: (payload: PdfChatHandoff) => void }) {
  const persist = variant === 'page';
  const kp = persist ? 'pdf' : 'cal.pdf';
  const [files, setFiles] = useIntakeState<File[]>(persist, `${kp}.files`, []);
  const [result, setResult] = useIntakeState<ExtractResponse | null>(persist, `${kp}.result`, null);
  const [draft, setDraft] = useIntakeState<Partial<ProjectCreateInput> | null>(
    persist,
    `${kp}.draft`,
    null,
  );
  const { data: mode } = useIntakeMode();
  const { data: me } = useCurrentUser();
  const isPm = me?.role === 'pm';
  const extract = useExtractPdfs();
  const create = useCreateProject();
  // Supervisors/admins plan the crew's tasks inline; PMs are requesters and
  // never see a task editor (the extractor's proposal still rides along hidden).
  const canEditTasks = me?.role === 'supervisor' || me?.role === 'admin';
  const [proposedTasks, setProposedTasks] = useIntakeState<IntakeProposedTask[]>(
    persist,
    `${kp}.proposedTasks`,
    [],
  );
  // Supervisor "Plan the work" authoring model — persisted in the session cache so
  // per-block roles/times/descriptions survive a screen switch (PDF has no server
  // draft; this covers client-side remounts).
  const [timingModel, setTimingModel] = useIntakeState<TimingModel | null>(
    persist,
    `${kp}.timingModel`,
    null,
  );
  // Inline PDF preview — which classified row is expanded (by filename).
  const [previewName, setPreviewName] = useState<string | null>(null);
  const previewUrl = useMemo(() => {
    if (!previewName) return null;
    const file = files.find((x) => x.name === previewName);
    return file ? URL.createObjectURL(file) : null;
  }, [previewName, files]);
  useEffect(
    () => () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    },
    [previewUrl],
  );

  // Report unsaved content so the modal can guard an accidental close.
  const dirty = files.length > 0 || !!draft;
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  const onDrop = useCallback(
    (accepted: File[]) => {
      const next = [...files];
      for (const f of accepted) {
        if (next.length >= 5) break;
        if (next.some((x) => x.name === f.name && x.size === f.size)) continue;
        next.push(f);
      }
      setFiles(next);
      // Reset previous extraction when files change
      setResult(null);
      setDraft(null);
      setProposedTasks([]);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [files],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/pdf': ['.pdf'] },
    maxFiles: 5,
    maxSize: 25 * 1024 * 1024,
  });

  async function handleExtract() {
    if (files.length === 0) {
      toast.error('Add at least one PDF.');
      return;
    }
    try {
      const r = await extract.mutateAsync(files);
      setResult(r);
      // Overlay the calendar slot's schedule so the drawn day/time shows in the
      // editable timing fields (the slot wins over what the extractor inferred).
      setDraft(r.draft ? applySeed(r.draft, seed) : null);
      // Seed the supervisor's editable task plan from the extractor's
      // line-item-aware proposal. Flexible task creation (Leo, demo #7): no
      // kind-default fallback — when the docs yield no tasks the studio starts
      // bare (one skeleton) and the supervisor opts in via "Suggest tasks".
      if (canEditTasks && r.draft) {
        setProposedTasks(r.proposed_tasks ?? []);
      }
      if (!r.draft) {
        toast.warning(
          'No project draft could be derived. Try adding a sales order or design package.',
        );
      } else {
        toast.success(
          `Extracted ${r.files.filter((f) => !f.error).length} of ${r.files.length} files.`,
        );
      }
    } catch (err) {
      toast.error(`Extraction failed: ${(err as Error).message}`);
    }
  }

  async function handleCreate() {
    // The slot was overlaid onto `draft` at extraction, so submit as-is —
    // re-applying here would clobber timing the user adjusted in the review.
    const prepared = draftToCreateInput(draft, 'pdf_upload');
    if (!prepared.ok) {
      toast.error(prepared.error);
      return;
    }
    try {
      // Task plan → proposed_tasks (materialized server-side; see POST /projects).
      // Supervisor/admin send their edited plan EXPLICITLY (even when empty, so an
      // intentionally task-less plan creates zero tasks — flexible task creation).
      // A PM sends nothing in the UI but the extractor's line-item-aware proposal
      // still rides along hidden (omit-when-empty), so the supervisor opens the
      // request with a rich starting breakdown.
      const extractorTasks = result?.proposed_tasks ?? [];
      const created = await create.mutateAsync({
        ...prepared.value,
        ...(canEditTasks
          ? { proposed_tasks: proposedTasks }
          : extractorTasks.length > 0
            ? { proposed_tasks: extractorTasks }
            : {}),
      });

      // Capture the PM's edits to the AI extraction as {extracted → corrected}
      // eval pairs (EX.9 / D.6). Best-effort, and only for real extractions —
      // stub/seed corrections aren't ground truth.
      if (result && (result.source === 'gemini' || result.source === 'mixed')) {
        const corrections = diffDraftCorrections(result.draft, draft);
        if (corrections.length > 0) {
          void recordExtractionCorrections({
            project_id: created.id,
            channel: 'pdf_upload',
            extraction_source: result.source,
            corrections,
          }).catch(() => {});
        }
      }

      // Persist the source PDFs as project documents (best-effort) so the
      // originals stay attached — carrying each file's detected kind +
      // structured extract from the extraction step.
      let docFailures = 0;
      for (const f of files) {
        const perFile = result?.files.find((x) => x.filename === f.name);
        const kind = perFile && perFile.kind !== 'unknown' ? perFile.kind : 'other';
        const extractedMetadata =
          perFile?.sales_order ?? perFile?.packing_slip ?? perFile?.design_package ?? null;
        try {
          await uploadProjectDocument({
            projectId: created.id,
            file: f,
            kind,
            extractedMetadata: extractedMetadata as Record<string, unknown> | null,
            pages: perFile?.design_package?.pages,
          });
        } catch {
          docFailures += 1;
        }
      }

      // Persist the install manifest (sales-order items + packing-slip
      // components) so the rich product list isn't discarded — it feeds the
      // crew checklist + the back-order flag. One bulk call, best-effort.
      const manifest: ProjectLineItemCreateInput[] = [];
      for (const pf of result?.files ?? []) {
        for (const li of pf.sales_order?.line_items ?? []) {
          manifest.push({
            source: 'sales_order',
            line_number: li.item_number ? Number(li.item_number) || null : null,
            product_code: li.product_code ?? null,
            description: li.description ?? '(item)',
            quantity: li.quantity ?? null,
            unit_price_cents: li.unit_price_cents ?? null,
            total_cents: li.total_cents ?? null,
          });
        }
        for (const li of pf.packing_slip?.line_items ?? []) {
          manifest.push({
            source: 'packing_slip',
            product_code: li.item_code ?? null,
            description: li.description ?? '(component)',
            quantity: li.ordered ?? null,
            backordered: li.backordered ?? null,
            shipped: li.shipped ?? null,
          });
        }
        // Design-package schedules → new (install) / reused / existing (EX.6).
        const dpSchedules = [
          ['design_new', pf.design_package?.new_products],
          ['design_reused', pf.design_package?.reused_products],
          ['design_existing', pf.design_package?.existing_products],
        ] as const;
        for (const [source, rows] of dpSchedules) {
          for (const it of rows ?? []) {
            manifest.push({
              source,
              product_code: it.model_number ?? null,
              description: it.description || '(item)',
              quantity: it.quantity ?? null,
            });
          }
        }
      }
      let manifestFailed = false;
      try {
        await persistProjectLineItems(created.id, manifest);
      } catch {
        manifestFailed = true;
      }

      const okParts: string[] = [];
      const docOk = files.length - docFailures;
      if (docOk > 0) okParts.push(`${docOk} document${docOk > 1 ? 's' : ''}`);
      if (manifest.length > 0 && !manifestFailed) {
        okParts.push(`${manifest.length} manifest item${manifest.length > 1 ? 's' : ''}`);
      }
      const failParts: string[] = [];
      if (docFailures > 0) failParts.push(`${docFailures} document(s)`);
      if (manifestFailed) failParts.push('the manifest');

      const noun = isPm ? 'Request submitted' : 'Project created';
      if (failParts.length > 0) {
        toast.warning(
          `${noun}${okParts.length ? ` with ${okParts.join(' · ')}` : ''}, but ${failParts.join(' and ')} couldn't be attached — add them from the project.`,
        );
      } else if (okParts.length > 0) {
        toast.success(`${noun} with ${okParts.join(' · ')} · ${created.title}`);
      } else {
        toast.success(`${noun} · ${created.title}`);
      }
      if (persist) clearIntakeSession('pdf');
      onDirtyChange?.(false);
      onCreated(created);
    } catch (err) {
      toast.error(`Failed to create project: ${(err as Error).message}`);
    }
  }

  // Hand off to the chat assistant to refine conversationally, carrying the
  // source files (for model context + persistence) and a recap of what was
  // extracted so the conversation has context.
  function continueInChat() {
    const d = draft ?? {};
    const recap: string[] = [];
    if (d.title) recap.push(d.title);
    if (d.sap_order_number) recap.push(`Order ${d.sap_order_number}`);
    const loc = [d.site_address?.line1, d.site_address?.city, d.site_address?.state]
      .filter(Boolean)
      .join(', ');
    if (loc) recap.push(loc);
    const names = files.map((f) => f.name).join(', ');
    const summary =
      `I've read your ${files.length} document${files.length > 1 ? 's' : ''}` +
      `${names ? ` (${names})` : ''}. ` +
      (recap.length ? `So far I have: ${recap.join(' · ')}. ` : '') +
      'Tell me anything to add or correct, or say "build the draft" when ready.';
    onContinueInChat({ summary, files });
  }

  function removeFile(idx: number) {
    setFiles(files.filter((_, i) => i !== idx));
    setResult(null);
    setDraft(null);
    setProposedTasks([]);
  }

  function patchDraft<K extends keyof ProjectCreateInput>(
    key: K,
    value: Partial<ProjectCreateInput>[K],
  ) {
    setDraft((d) => ({ ...(d ?? {}), [key]: value }));
  }

  return (
    <>
      {variant === 'page' && <IntakeHeader mode="pdf" isPm={isPm} />}

      {mode?.stub && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 flex items-start gap-2 text-xs">
          <TriangleAlert className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
          <div className="text-amber-900">
            <span className="font-semibold">Stub mode.</span> No{' '}
            <code className="font-mono">GOOGLE_GENAI_API_KEY</code> is set, so extractions return
            deterministic placeholder data keyed off the filename (the City of Grapevine sample files
            round-trip correctly). Set the env var to switch to live Gemini calls.
          </div>
        </div>
      )}

      {/* Dropzone */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div
            {...getRootProps()}
            className={cn(
              'border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors',
              isDragActive
                ? 'border-sky-400 bg-sky-50'
                : 'border-line hover:border-ink-faint hover:bg-fill-soft',
            )}
          >
            <input {...getInputProps()} />
            <Upload className="w-7 h-7 mx-auto text-ink-faint" />
            <div className="mt-2 text-sm font-medium">
              {isDragActive ? 'Drop PDFs to add' : 'Drag PDFs here, or click to select'}
            </div>
            <div className="text-[11px] text-ink-faint mt-1">
              Up to 5 files · 25 MB each · sales order + packing slip + design package recommended
            </div>
          </div>

          {files.length > 0 && (
            <ul className="space-y-1">
              {files.map((f, i) => (
                <li
                  key={i}
                  className="flex items-center gap-2 px-3 py-1.5 rounded border border-line"
                >
                  <FileText className="w-3.5 h-3.5 text-ink-faint shrink-0" />
                  <span className="text-sm truncate flex-1">{f.name}</span>
                  <span className="text-[11px] text-ink-faint shrink-0">
                    {(f.size / 1024).toFixed(0)} KB
                  </span>
                  <Button variant="ghost" size="icon" onClick={() => removeFile(i)} title="Remove">
                    <Trash2 className="w-3 h-3 text-status-blocked-fg" />
                  </Button>
                </li>
              ))}
            </ul>
          )}

          <div className="flex justify-end">
            <Button onClick={handleExtract} disabled={files.length === 0 || extract.isPending}>
              {extract.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              <Sparkles className="w-3.5 h-3.5" />
              Extract fields
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Extraction per-file results */}
      {result && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Per-file extraction</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {result.files.map((f, i) => {
              const isOpen = previewName === f.filename;
              const hasFile = files.some((x) => x.name === f.filename);
              return (
                <div key={i} className="border-b border-line last:border-0">
                  <button
                    type="button"
                    onClick={() => setPreviewName(isOpen ? null : f.filename)}
                    disabled={!hasFile}
                    aria-expanded={isOpen}
                    className="flex w-full items-center gap-2 py-1.5 text-left text-sm enabled:hover:bg-fill-soft disabled:cursor-default -mx-2 px-2 rounded"
                  >
                    {f.error ? (
                      <TriangleAlert className="w-4 h-4 text-status-blocked-fg shrink-0" />
                    ) : (
                      <CheckCircle2 className="w-4 h-4 text-status-complete-fg shrink-0" />
                    )}
                    <span className="font-mono text-xs truncate flex-1">{f.filename}</span>
                    <span
                      className={cn('text-[10px] px-1.5 py-0.5 rounded border', KIND_TINT[f.kind])}
                    >
                      {KIND_LABEL[f.kind]}
                    </span>
                    <span
                      className={cn(
                        'text-[10px] font-mono',
                        f.source === 'failed'
                          ? 'text-status-blocked-fg font-semibold'
                          : 'text-ink-faint',
                      )}
                      title={f.error ?? undefined}
                    >
                      {f.source}
                    </span>
                    {hasFile && (
                      <ChevronDown
                        className={cn(
                          'w-3.5 h-3.5 text-ink-faint shrink-0 transition-transform',
                          isOpen && 'rotate-180',
                        )}
                      />
                    )}
                  </button>
                  {isOpen && previewUrl && (
                    <div className="pb-2 space-y-1">
                      <iframe
                        src={previewUrl}
                        title={`Preview of ${f.filename}`}
                        className="w-full h-[460px] rounded-md border border-line bg-fill-soft"
                      />
                      <a
                        href={previewUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[11px] text-accent hover:underline"
                      >
                        Open in a new tab ↗
                      </a>
                    </div>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Hand-off to chat — refine conversationally instead of in the form */}
      {draft && (
        <div className="flex items-center justify-between gap-3 rounded-md border border-line bg-fill-soft px-3 py-2">
          <p className="text-xs text-ink-dim">
            Prefer to talk it through? Continue in chat — your files come along and the assistant can
            read them.
          </p>
          <Button variant="outline" size="sm" onClick={continueInChat} className="shrink-0">
            <MessageSquareText className="w-3.5 h-3.5" />
            Continue in chat
          </Button>
        </div>
      )}

      {/* Review draft (shared with chat intake) */}
      {draft && (
        <ProjectDraftReview
          draft={draft}
          onPatch={patchDraft}
          onSubmit={handleCreate}
          submitLabel={isPm ? 'Submit request' : 'Create project'}
          submitting={create.isPending}
          source={result?.source}
          warnings={result?.warnings}
          proposedTasks={canEditTasks ? proposedTasks : undefined}
          onProposedTasksChange={canEditTasks ? setProposedTasks : undefined}
          initialTimingModel={canEditTasks ? timingModel : undefined}
          onTimingModelChange={canEditTasks ? setTimingModel : undefined}
          allowSignoff={canEditTasks}
          onReset={() => {
            setFiles([]);
            setResult(null);
            setDraft(null);
            setProposedTasks([]);
            setTimingModel(null);
          }}
        />
      )}
    </>
  );
}
